import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProperty } from '../context/PropertyContext';
import PropertyCard from '../components/PropertyCard';
import PropertyMap from '../components/PropertyMap';
import SearchForm from '../components/SearchForm';
import { Property } from '../types';
import { Filter, Grid, List, SlidersHorizontal, Bed, Car, Ruler, Eye, MapPin } from 'lucide-react';

const SearchPage: React.FC = () => {
  const { filteredProperties, searchFilters } = useProperty();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('list');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  const hasActiveFilters = () => {
    return searchFilters.location || 
           searchFilters.category || 
           searchFilters.type || 
           searchFilters.bedrooms > 0 || 
           searchFilters.suites > 0 || 
           searchFilters.parkingSpaces > 0 || 
           searchFilters.priceMin > 0 || 
           searchFilters.priceMax < 10000000;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handlePropertySelect = (property: Property) => {
    setSelectedProperty(property);
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'Venda': return 'bg-green-100 text-green-800';
      case 'Aluguel': return 'bg-blue-100 text-blue-800';
      case 'Venda/Aluguel': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionLabel = (type: string) => {
    return type; // Já está em português
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Buscar Imóveis
          </h1>
          <p className="text-gray-600">
            Encontre o imóvel perfeito usando nossos filtros avançados
          </p>
        </div>

        {/* Search Form */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span>Filtros</span>
              {hasActiveFilters() && (
                <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                  Ativos
                </span>
              )}
            </button>

            {/* View Toggle */}
            <div className="flex bg-white rounded-lg shadow-sm p-1 border border-gray-300">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                  viewMode === 'grid'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Grid className="w-4 h-4" />
                <span className="hidden sm:inline">Grade</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                  viewMode === 'list'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Lista</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 py-2 rounded-md transition-colors flex items-center space-x-2 ${
                  viewMode === 'map'
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <MapPin className="w-4 h-4" />
                <span className="hidden sm:inline">Mapa</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mb-6">
              <SearchForm />
            </div>
          )}
        </div>

        {/* Results Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800">
              {filteredProperties.length} imóveis encontrados
            </h2>
            
            {viewMode !== 'map' && (
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                <option value="recent">Mais recentes</option>
                <option value="price-asc">Menor preço</option>
                <option value="price-desc">Maior preço</option>
                <option value="area-asc">Menor área</option>
                <option value="area-desc">Maior área</option>
              </select>
            )}
          </div>
        </div>

        {/* Content */}
        {filteredProperties.length > 0 ? (
          viewMode === 'map' ? (
            // Map View
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 min-h-[80vh]">
              {/* Map - Full width on mobile, 2/3 on desktop */}
              <div className="h-[60vh] md:h-[70vh] lg:h-[80vh] lg:col-span-2 order-2 lg:order-1">
                <PropertyMap
                  properties={filteredProperties}
                  selectedProperty={selectedProperty}
                  onPropertySelect={handlePropertySelect}
                  className="w-full h-full"
                />
              </div>

              {/* Property List Sidebar */}
              <div className="bg-white rounded-lg shadow-lg overflow-hidden h-[40vh] lg:h-[80vh] order-1 lg:order-2">
                <div className="p-3 lg:p-4 border-b border-gray-200">
                  <h3 className="text-base lg:text-lg font-semibold text-gray-800">
                    Resultados da Busca
                  </h3>
                  <p className="text-xs lg:text-sm text-gray-600">
                    {filteredProperties.length} imóveis encontrados
                  </p>
                </div>
                
                <div className="overflow-y-auto h-full max-h-[calc(40vh-60px)] lg:max-h-[calc(80vh-80px)]">
                  <div className="p-2 lg:p-4 space-y-2 lg:space-y-4">
                    {filteredProperties.map((property) => (
                      <div
                        key={property.listingId}
                        className={`cursor-pointer rounded-lg border-2 transition-colors ${
                          selectedProperty?.listingId === property.listingId
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                        onClick={() => handlePropertySelect(property)}
                      >
                        <div className="p-2 lg:p-3">
                          <div className="flex items-start space-x-2 lg:space-x-3">
                            <img
                              src={
                                property.media.images[0]?.url ||
                                'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=150'
                              }
                              alt={property.title}
                              className="w-12 h-12 lg:w-16 lg:h-16 object-cover rounded-lg flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs lg:text-sm font-medium text-gray-800 line-clamp-2">
                                {property.title}
                              </h4>
                              <p className="text-xs text-gray-600 mt-1">
                                {property.location.neighborhood}, {property.location.city}
                              </p>
                              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mt-1 lg:mt-2 space-y-1 lg:space-y-0">
                                <div className="flex flex-col">
                                  {property.transactionType === 'Venda/Aluguel' ? (
                                    <div className="space-y-1">
                                      <span className="text-xs lg:text-sm font-bold text-green-600">
                                        Venda: {formatPrice(property.listPrice)}
                                      </span>
                                      <span className="text-xs lg:text-sm font-bold text-blue-600">
                                        Locação: {formatPrice(property.rentalPrice || 0)}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs lg:text-sm font-bold text-orange-600">
                                      {property.transactionType === 'Aluguel' ? formatPrice(property.rentalPrice || 0) : formatPrice(property.listPrice)}
                                    </span>
                                  )}
                                  
                                  {/* Condomínio e IPTU */}
                                  <div className="flex flex-col space-y-0.5 text-xs text-gray-500 mt-0.5">
                                    {property.propertyAdministrationFee && property.propertyAdministrationFee > 0 && (
                                      <span>Cond: {formatPrice(property.propertyAdministrationFee)}</span>
                                    )}
                                    {property.yearlyTax && property.yearlyTax > 0 && (
                                      <span>IPTU: {formatPrice(property.yearlyTax)}/mês</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                  <span>{property.bedrooms}q</span>
                                  <span>•</span>
                                  <span>{property.garage.quantity}v</span>
                                  <span>•</span>
                                  <span>{property.livingArea}m²</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            // Grid View
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map((property) => (
                <PropertyCard key={property.listingId} property={property} />
              ))}
            </div>
          ) : (
            // List View (Table)
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Imóvel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Localização
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Detalhes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Preço
                      </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                         Condomínio/IPTU
                       </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredProperties.map((property) => (
                      <tr key={property.listingId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-16 w-16">
                              <img
                                className="h-16 w-16 rounded-lg object-cover"
                                src={property.media.images[0]?.url || 'https://images.pexels.com/photos/106399/pexels-photo-106399.jpeg?auto=compress&cs=tinysrgb&w=800'}
                                alt={property.title}
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 line-clamp-2 max-w-xs">
                                <Link to={`/imovel/${property.listingId}`} className="hover:underline">
                                  {property.title}
                                </Link>
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionColor(property.transactionType)}`}>
                                  {getTransactionLabel(property.transactionType)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {property.propertyCode}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                         <td className="px-6 py-4 whitespace-nowrap">
                           <div className="text-sm text-gray-900">
                             {property.location.neighborhood}
                           </div>
                           <div className="text-sm text-gray-500">
                             {property.location.city}
                           </div>
                         </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-4 text-sm text-gray-600">
                            <div className="flex items-center">
                              <Bed className="w-4 h-4 mr-1" />
                              <span>{property.bedrooms}</span>
                            </div>
                            <div className="flex items-center">
                              <Car className="w-4 h-4 mr-1" />
                              <span>{property.garage.quantity}</span>
                            </div>
                            <div className="flex items-center">
                              <Ruler className="w-4 h-4 mr-1" />
                              <span>{property.livingArea}m²</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {property.transactionType === 'Venda/Aluguel' ? (
                            <div className="space-y-1">
                              <div className="text-sm font-bold text-green-600">
                                Venda: {formatPrice(property.listPrice)}
                              </div>
                              <div className="text-sm font-bold text-blue-600">
                                Locação: {formatPrice(property.rentalPrice || 0)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-bold text-orange-600">
                              {property.transactionType === 'Aluguel' ? formatPrice(property.rentalPrice || 0) : formatPrice(property.listPrice)}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {new Date(property.listDate).toLocaleDateString('pt-BR')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {property.propertyAdministrationFee ? (
                              <>Cond: {formatPrice(property.propertyAdministrationFee)}</>
                            ) : (
                              'Cond: Isento'
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                             {property.yearlyTax ? (
                               <>IPTU: {formatPrice(property.yearlyTax)}/mês</>
                             ) : (
                               'IPTU: Consultar'
                             )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/imovel/${property.listingId}`}
                              className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                              title="Ver Detalhes"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Nenhum imóvel encontrado
            </h3>
            <p className="text-gray-600 mb-4">
              Tente ajustar seus filtros para encontrar mais opções
            </p>
            <button
              onClick={() => setShowFilters(true)}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Ajustar Filtros
            </button>
          </div>
        )}

        {/* Selected Property Details */}
        {selectedProperty && viewMode === 'map' && (
          <div className="mt-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Imóvel Selecionado
              </h3>
              <div className="max-w-md">
                <PropertyCard property={selectedProperty} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;